import { DalantStudentItem } from '../../../constants/types/lms/dalant/type';
import TextWinds from '../../common/TextWinds';
import { Button, Checkbox, Input, InputNumber } from 'antd';
import { formatToKstDate } from '../../../api/utils/util-func';
import { DalantFormState } from './DalantByClassContents';

interface DalantByClassCardProps {
  student: DalantStudentItem;
  isOpened: boolean;
  isSelected: boolean;
  isPending: boolean;
  formState: DalantFormState;
  onSelect: (checked: boolean) => void;
  onToggleOpen: () => void;
  onChangeFormState: (value: Partial<DalantFormState>) => void;
  onSubmit: () => void;
}

/**
 * 개편 LMS (교사) - 달란트 관리 페이지 > 반별 학생 달란트 > 반별 학생 달란트 카드
 */
const DalantByClassCard = ({
  student,
  isOpened,
  isSelected,
  isPending,
  formState,
  onSelect,
  onToggleOpen,
  onChangeFormState,
  onSubmit,
}: DalantByClassCardProps) => {
  const isNegative = student.totalPoints < 0;

  return (
    <article
      className={[
        'relative flex min-h-[130px] flex-col overflow-hidden rounded-xl border bg-white transition-shadow',
        isSelected
          ? 'border-purple5 ring-1 ring-purple2'
          : isOpened
            ? 'border-purple5 shadow-md'
            : 'border-gray3 shadow-sm hover:shadow-md',
      ].join(' ')}
    >
      <div className="absolute left-3 top-3 z-10">
        <Checkbox
          checked={isSelected}
          onChange={(event) => onSelect(event.target.checked)}
        />
      </div>

      <div className="flex flex-1 flex-col items-center px-4 py-5 text-center">
        <TextWinds type="title_h1" color="gray9" className="mt-2 font-bold">
          {student.studentName}
        </TextWinds>

        <TextWinds
          type="title_h2"
          className={[
            'mt-1 font-extrabold',
            isNegative ? 'text-red-500' : 'text-green-600',
          ].join(' ')}
        >
          {student.totalPoints > 0 ? '+' : ''}
          {student.totalPoints.toLocaleString()}
        </TextWinds>

        <TextWinds type="caption1" color="gray6" className="mt-1 block">
          최근 변경일{' '}
          {student.lastEarnedAt
            ? formatToKstDate(student.lastEarnedAt, 'YYYY.MM.DD HH:mm')
            : '-'}
        </TextWinds>

        <Button
          type="primary"
          className={[
            'mt-4 w-full rounded-lg',
            isOpened
              ? '!border-gray4 !bg-white !text-gray8'
              : '!border-purple5 !bg-purple5 !text-white',
          ].join(' ')}
          onClick={onToggleOpen}
        >
          {isOpened ? '입력 닫기' : '✏️ 달란트 입력'}
        </Button>
      </div>

      {isOpened && (
        <div className="border-t border-gray2 bg-gray0 px-4 py-4">
          <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-gray3 bg-white">
            <button
              type="button"
              className={[
                'h-9 text-sm font-bold transition-colors',
                formState.actionType === 'EARN'
                  ? 'bg-purple5 text-white'
                  : 'text-gray7 hover:bg-gray1',
              ].join(' ')}
              onClick={() =>
                onChangeFormState({
                  actionType: 'EARN',
                })
              }
            >
              적립
            </button>

            <button
              type="button"
              className={[
                'h-9 border-l border-gray3 text-sm font-bold transition-colors',
                formState.actionType === 'DEDUCT'
                  ? 'bg-red-500 text-white'
                  : 'text-gray7 hover:bg-gray1',
              ].join(' ')}
              onClick={() =>
                onChangeFormState({
                  actionType: 'DEDUCT',
                })
              }
            >
              차감
            </button>
          </div>

          <InputNumber
            className="mt-3 w-full"
            min={1}
            precision={0}
            placeholder="달란트 수량"
            value={formState.points || null}
            onChange={(value) =>
              onChangeFormState({
                points: value ?? 0,
              })
            }
          />

          <Input.TextArea
            className="mt-2"
            rows={2}
            maxLength={100}
            showCount
            placeholder="적립 또는 차감 사유를 입력해주세요."
            value={formState.reason}
            onChange={(event) =>
              onChangeFormState({
                reason: event.target.value,
              })
            }
          />

          <Button
            type="primary"
            danger={formState.actionType === 'DEDUCT'}
            loading={isPending}
            className={[
              'mt-6 w-full rounded-lg',
              formState.actionType === 'EARN'
                ? '!border-purple5 !bg-purple5'
                : '',
            ].join(' ')}
            onClick={onSubmit}
          >
            {formState.actionType === 'EARN' ? '달란트 적립' : '달란트 차감'}
          </Button>
        </div>
      )}
    </article>
  );
};

export default DalantByClassCard;
